'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

type AuthTab = 'login' | 'signup';
type Role = 'player' | 'coach' | 'academy';
type Sport = 'cricket' | 'badminton' | 'both';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupFormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: Role;
  sport: Sport;
  agreeTerms: boolean;
}

const demoCredentials = [
  { role: 'Player', email: 'rahul.patel@rohitnextai.com', password: 'Player@2026' },
  { role: 'Admin', email: 'admin@rohitnextai.com', password: 'Admin@2026' },
];

export default function AuthForm() {
  const [tab, setTab] = useState<AuthTab>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role>('player');
  const [selectedSport, setSelectedSport] = useState<Sport>('cricket');
  const router = useRouter();
  const { signIn, signUp } = useAuth();

  const loginForm = useForm<LoginFormData>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupFormData>({
    defaultValues: { fullName: '', email: '', password: '', confirmPassword: '', role: 'player', sport: 'cricket', agreeTerms: false }
  });

  const handleLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      const role = result?.user?.user_metadata?.role ?? 'player';
      toast.success(role === 'admin' ? 'Welcome back, Admin!' : 'Welcome back!');
      router.push(role === 'admin' ? '/admin-dashboard' : '/student-player-dashboard');
      router.refresh();
    } catch (error: any) {
      const msg = error?.message || 'Invalid credentials';
      if (msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('credentials') || msg.toLowerCase().includes('password')) {
        loginForm.setError('email', { message: 'Invalid email or password. Try the demo accounts below.' });
      } else {
        toast.error(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupSubmit = async (data: SignupFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        role: data.role,
        sport: data.sport,
      });
      toast.success('Account created! You can now sign in.');
      setTab('login');
      loginForm.setValue('email', data.email);
    } catch (error: any) {
      const msg = error?.message || 'Signup failed';
      if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exists')) {
        signupForm.setError('email', { message: 'This email is already registered. Please sign in.' });
      } else {
        toast.error(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const autofillCredentials = (email: string, password: string) => {
    loginForm.setValue('email', email);
    loginForm.setValue('password', password);
    setTab('login');
    toast.info('Credentials filled — click Sign In');
  };

  return (
    <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-12 xl:px-16 py-10 overflow-y-auto">
      <div className="w-full max-w-md mx-auto">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold">R</span>
          </div>
          <span className="font-bold text-foreground text-lg">RohitNextAI</span>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-foreground mb-1">
            {tab === 'login' ? 'Sign in to your account' : 'Create your account'}
          </h2>
          <p className="text-muted-foreground text-sm">
            {tab === 'login' ? 'Access your AI analysis reports and progress.' : 'Start analyzing your game footage with AI.'}
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex bg-muted rounded-xl p-1 mb-6">
          {(['login', 'signup'] as AuthTab[]).map((t) => (
            <button
              key={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                tab === t
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'login' ? 'Sign In' : 'Sign Up'}
            </button>
          ))}
        </div>

        {/* LOGIN FORM */}
        {tab === 'login' && (
          <form onSubmit={loginForm.handleSubmit(handleLoginSubmit)} className="space-y-4 animate-fade-in">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Email address</label>
              <input
                type="email"
                {...loginForm.register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' }
                })}
                placeholder="you@example.com"
                className="w-full bg-input border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              />
              {loginForm.formState.errors.email && (
                <p className="mt-1.5 text-xs text-danger">{loginForm.formState.errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...loginForm.register('password', { required: 'Password is required' })}
                  placeholder="••••••••"
                  className="w-full bg-input border border-border rounded-lg px-4 py-3 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {loginForm.formState.errors.password && (
                <p className="mt-1.5 text-xs text-danger">{loginForm.formState.errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" {...loginForm.register('remember')} className="w-4 h-4 rounded border-border bg-input accent-primary" />
                <span className="text-sm text-muted-foreground">Remember me</span>
              </label>
              <button type="button" className="text-sm text-primary hover:underline">
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary text-primary-foreground font-semibold py-3 rounded-lg transition-all duration-150 hover:opacity-90 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ minHeight: '48px' }}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  <span>Signing in...</span>
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        )}

        {/* SIGNUP FORM */}
        {tab === 'signup' && (
          <form onSubmit={signupForm.handleSubmit(handleSignupSubmit)} className="space-y-4 animate-fade-in">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Full name</label>
              <input
                type="text"
                {...signupForm.register('fullName', { required: 'Full name is required', minLength: { value: 2, message: 'Name too short' } })}
                placeholder="Rahul Patel"
                className="w-full bg-input border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              />
              {signupForm.formState.errors.fullName && (
                <p className="mt-1.5 text-xs text-danger">{signupForm.formState.errors.fullName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Email address</label>
              <input
                type="email"
                {...signupForm.register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' } })}
                placeholder="you@example.com"
                className="w-full bg-input border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              />
              {signupForm.formState.errors.email && (
                <p className="mt-1.5 text-xs text-danger">{signupForm.formState.errors.email.message}</p>
              )}
            </div>

            {/* Role selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">I am a</label>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { value: 'player', label: 'Player', icon: '🏏' },
                  { value: 'coach', label: 'Coach', icon: '📋' },
                  { value: 'academy', label: 'Academy', icon: '🏫' },
                ] as { value: Role; label: string; icon: string }[]).map((r) => (
                  <button
                    key={`role-${r.value}`}
                    type="button"
                    onClick={() => {
                      setSelectedRole(r.value);
                      signupForm.setValue('role', r.value);
                    }}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-lg border text-xs font-medium transition-all duration-150 ${
                      selectedRole === r.value
                        ? 'border-primary bg-primary/10 text-primary' :'border-border bg-input text-muted-foreground hover:border-border/80 hover:text-foreground'
                    }`}
                  >
                    <span className="text-lg">{r.icon}</span>
                    <span>{r.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Sport selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Primary sport</label>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { value: 'cricket', label: 'Cricket', icon: '🏏' },
                  { value: 'badminton', label: 'Badminton', icon: '🏸' },
                  { value: 'both', label: 'Both', icon: '⚡' },
                ] as { value: Sport; label: string; icon: string }[]).map((s) => (
                  <button
                    key={`sport-${s.value}`}
                    type="button"
                    onClick={() => {
                      setSelectedSport(s.value);
                      signupForm.setValue('sport', s.value);
                    }}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-lg border text-xs font-medium transition-all duration-150 ${
                      selectedSport === s.value
                        ? 'border-primary bg-primary/10 text-primary' :'border-border bg-input text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <span className="text-lg">{s.icon}</span>
                    <span>{s.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...signupForm.register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Minimum 8 characters' },
                    pattern: { value: /(?=.*[A-Z])(?=.*[0-9])/, message: 'Must include uppercase and a number' }
                  })}
                  placeholder="Min. 8 characters"
                  className="w-full bg-input border border-border rounded-lg px-4 py-3 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                  )}
                </button>
              </div>
              {signupForm.formState.errors.password && (
                <p className="mt-1.5 text-xs text-danger">{signupForm.formState.errors.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Confirm password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                  placeholder="Re-enter password"
                  className="w-full bg-input border border-border rounded-lg px-4 py-3 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showConfirmPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                  )}
                </button>
              </div>
              {signupForm.formState.errors.confirmPassword && (
                <p className="mt-1.5 text-xs text-danger">{signupForm.formState.errors.confirmPassword.message}</p>
              )}
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                {...signupForm.register('agreeTerms', { required: 'You must accept the terms' })}
                className="w-4 h-4 mt-0.5 rounded border-border bg-input accent-primary flex-shrink-0"
              />
              <span className="text-xs text-muted-foreground">
                I agree to the{' '}
                <span className="text-primary hover:underline cursor-pointer">Terms of Service</span>{' '}
                and{' '}
                <span className="text-primary hover:underline cursor-pointer">Privacy Policy</span>
              </span>
            </label>
            {signupForm.formState.errors.agreeTerms && (
              <p className="text-xs text-danger">{signupForm.formState.errors.agreeTerms.message}</p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary text-primary-foreground font-semibold py-3 rounded-lg transition-all duration-150 hover:opacity-90 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ minHeight: '48px' }}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                  <span>Creating account...</span>
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>
        )}

        {/* Demo credentials */}
        <div className="mt-6 p-4 bg-muted/50 border border-border rounded-xl">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Demo Accounts
          </p>
          <div className="space-y-2">
            {demoCredentials.map((cred) => (
              <div key={`demo-${cred.role.toLowerCase()}`} className="flex items-center justify-between gap-2 bg-card rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded mr-2 ${cred.role === 'Admin' ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'}`}>
                    {cred.role}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono truncate">{cred.email}</span>
                </div>
                <button
                  type="button"
                  onClick={() => autofillCredentials(cred.email, cred.password)}
                  className="flex-shrink-0 text-xs bg-primary/10 text-primary hover:bg-primary/20 px-2 py-1 rounded font-medium transition-colors"
                >
                  Use
                </button>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          {tab === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <button
            type="button"
            onClick={() => setTab(tab === 'login' ? 'signup' : 'login')}
            className="text-primary hover:underline font-medium"
          >
            {tab === 'login' ? 'Sign up free' : 'Sign in'}
          </button>
        </p>
      </div>
    </div>
  );
}